import React from "react";
import styled from "styled-components";
import { tokens } from "../../public/tokens";

const tones = {
    warning: {
        bg: tokens.colors.states.warning["100"],
        border: tokens.colors.states.warning["300"],
        fg: tokens.colors.states.warning["800"],
        icon: "exclamation-triangle",
    },
    success: {
        bg: tokens.colors.states.success["100"],
        border: tokens.colors.states.success["300"],
        fg: tokens.colors.states.success["800"],
        icon: "check-circle",
    },
    info: {
        bg: tokens.colors.states.info["100"],
        border: tokens.colors.states.info["300"],
        fg: tokens.colors.states.info["800"],
        icon: "info-circle",
    },
    locked: {
        bg: tokens.colors.states.warning["100"],
        border: tokens.colors.states.warning["500"],
        fg: tokens.colors.neutral.grey["900"],
    },
};

const StyledBanner = styled.div(({ $tone = "info", $hasBadge = false }) => {
    const tone = tones[$tone] || tones.info;
    return {
        width: "100%",
        display: "flex",
        alignItems: $hasBadge ? "center" : "flex-start",
        gap: $hasBadge ? tokens.globals["spacing-4"] : tokens.globals["spacing-2-5"],
        padding: $tone === "locked"
            ? tokens.globals["spacing-3-5"]
            : `${tokens.globals["spacing-md"]} ${tokens.globals["spacing-lg"]}`,
        background: tone.bg,
        border: `${tokens.globals["spacing-px"]} solid ${tone.border}`,
        borderRadius: tokens.globals["border-radius"],
    };
});

const StyledIcon = styled.i(({ $tone = "info" }) => ({
    color: (tones[$tone] || tones.info).fg,
    fontSize: tokens.typography.fontSize["lg-14px"],
    lineHeight: tokens.typography.lineHeights["130"],
    flexShrink: 0,
    marginTop: "2px",
}));

const StyledText = styled.div(({ $tone = "info" }) => ({
    color: (tones[$tone] || tones.info).fg,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontWeight: tokens.typography.fontWeights.medium,
    lineHeight: tokens.typography.lineHeights["130"],
}));

/**
 * Persistent, always-visible status banner (locked-version warning, "all modules assigned"
 * success, etc.) — distinct from `Modal.js` (one-off click-through dialog) and a `react-toastify`
 * toast (transient); neither fits a state that stays on screen for as long as it's true.
 * @param {"warning"|"success"|"info"|"locked"} [props.tone="info"] Colour + default icon.
 * @param {string} [props.icon] Font Awesome icon name; overrides the tone's default icon.
 * @param {React.ReactNode} [props.badge] Leading element (e.g. a `Badge` pill) rendered before
 * the message instead of/alongside the icon; when present the banner centers its items and uses
 * a wider gap to match a single-line pill+text layout.
 * Also: children, className (self-explanatory).
 */
function Banner({ tone = "info", icon, badge, children, className, ...props }) {
    const resolvedIcon = icon || (tones[tone] || tones.info).icon;

    return (
        <StyledBanner {...props} $tone={tone} $hasBadge={Boolean(badge)} className={className} role="status">
            {badge}
            {resolvedIcon && <StyledIcon $tone={tone} className={`fal fa-${resolvedIcon}`} aria-hidden="true" />}
            <StyledText $tone={tone}>{children}</StyledText>
        </StyledBanner>
    );
}

Banner.displayName = "Banner";

export default Banner;
